/**
 * Bucket view (SuperAdmin Audio Manager tab) — a storage explorer.
 *
 * Right: browse `audio`, `backup-audio` and `transcription-backups` like the
 * Supabase dashboard — bucket → provider → year → day → files, any day, any
 * provider. In a day folder each file shows the chart that links it (that
 * provider's rows for that day), the patient its filename names, and its
 * transcripts; audio plays inline, JSON backups open as text.
 *
 * Left: the patients scheduled on the Day picked above — the move targets.
 * Drag files onto a patient, or use "Move to…" / select several. A move goes
 * through the backend (dry run first, shown in the confirm dialog):
 *  - audio   → POST /api/transcription-backup/move-audio/ — the file is
 *              renamed for the target and filed under the target's provider
 *              and VISIT DAY; its chart entry, transcripts and transcript
 *              backups move with it. When the explorer doesn't know which
 *              chart links it, the backend finds it.
 *  - JSON    → POST /api/transcription-backup/move-transcript/ — re-filed for
 *              the target and put on the target's chart as a transcript.
 *
 * "Suggested fixes" (top) lists the selected day's recordings whose filename
 * names a different scheduled patient than the chart they're on.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  ButtonGroup,
  Checkbox,
  Collapse,
  Flex,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Text,
  VStack,
  Wrap,
  WrapItem,
  useToast,
} from "@chakra-ui/react";
import { getSupabase, supabasePublic } from "../../components/supabaseClient";
import { getBackendURL, supabaseUrl, tableName } from "../../components/env";
import AudioPlayer from "../../components/table/audio/AudioPlayer";
import { clearBackupCache } from "../../components/AudioManagerModal";
import { transcriptBelongsToAudio, transcriptShowsAsLive } from "../../utils/audioMetadata";
import { audioMillis, transcriptToText } from "../medstudent/ClinicAudiosModal";

export const BUCKETS = ["audio", "backup-audio", "transcription-backups"];
const AUDIO_FILE_RE = /\.(webm|mp3|wav|m4a|ogg|mp4)$/i;
const OWNER_RE = /^(.*?)(?:[_-](?:Audio|Backup|Recording)\d+|_Rolling)(?:[_-].*)?$/i;
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
// Moved files: `<Name>-<EHR>_MOVED_AudioN_…` (backend audio_move.MOVED_MARKER).
const MOVED_SUFFIX_RE = /[_-]MOVED$/i;
/** True for a file the Bucket view moved between patients. */
export const isMovedName = (name) => /[_-]MOVED[_-]/i.test(String(name || ""));
const PAGE = 500;
const alnum = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
export const rowKey = (row) => (row ? `${row._schema}::${row.id}` : "");
/** The PERSON behind a schedule row (rows differ per visit day): EHR, else name. */
export const patientIdentity = (row) => {
  if (!row) return "";
  const ehr = String(row.EHR_ID || "").trim().toLowerCase();
  return ehr ? `ehr:${ehr}` : `name:${alnum(row.patient_name)}`;
};

/** { bucket, path } of a stored file, from its URL or a scan's storage_path. */
export function storageRef(audio) {
  // The entry's own stored file wins: mobile-recorder entries keep it in
  // bucket + path while `url` may be a temporary signed link to the backup.
  const own = audio?.storage_path || audio?.path;
  if (own) return { bucket: audio.bucket || "audio", path: String(own).replace(/^\/+/, "") };
  return urlRef(audio?.url || audio?.audio_url);
}

/** { bucket, path } of a public / signed / authenticated Storage URL. */
export function urlRef(url) {
  const raw = String(url || "").split("?")[0];
  const m = raw.match(/\/storage\/v1\/object\/(?:public|sign|authenticated)\/([^/]+)\/(.+)$/);
  if (!m) return { bucket: null, path: null };
  let path = m[2];
  try {
    path = decodeURIComponent(path);
  } catch {
    /* keep encoded */
  }
  return { bucket: m[1], path };
}

/**
 * Every stored file a chart entry points at, as "bucket/path" keys: its url
 * (public or signed) AND its bucket + path / storage_path. Reading only the
 * url made a mobile recording's real file look like it was on no chart.
 */
export function entryFileKeys(audio) {
  const keys = new Set();
  const add = ({ bucket, path }) => bucket && path && keys.add(`${bucket}/${path}`);
  add(urlRef(audio?.url || audio?.audio_url));
  for (const own of [audio?.path, audio?.storage_path]) {
    if (own) add({ bucket: audio.bucket || "audio", path: String(own).replace(/^\/+/, "") });
  }
  return [...keys];
}

/** A temporary (token) Storage link rather than the permanent public one. */
export const isSignedUrl = (url) => /\/storage\/v1\/object\/(?:sign|authenticated)\//.test(String(url || ""));

export const publicUrl = (bucket, path) =>
  `${String(supabaseUrl || "").replace(/\/$/, "")}/storage/v1/object/public/${bucket}/${String(path)
    .split("/")
    .map(encodeURIComponent)
    .join("/")}`;

/**
 * Which patient a stored filename names (`<Name>-<EHR>_AudioN…`, also
 * `…_AudioN.json`): a unique EHR match wins, else the longest patient name
 * the filename starts with. null when it names none of `rows`.
 */
export function matchFileOwner(leaf, rows) {
  const stem = String(leaf || "").replace(AUDIO_FILE_RE, "").replace(/(_copilot)?\.json$/i, "");
  const m = stem.match(OWNER_RE);
  const part = (m ? m[1] : "").replace(MOVED_SUFFIX_RE, "");
  if (!part) return null;
  const lower = part.toLowerCase();
  const byEhr = (rows || []).filter((r) => {
    const ehr = String(r.EHR_ID || "").trim().replace(/[^\w-]/g, "").toLowerCase();
    return ehr && lower.endsWith(`-${ehr}`);
  });
  // One person (possibly several visit rows — the first, i.e. the caller's preferred, wins).
  if (byEhr.length && new Set(byEhr.map(patientIdentity)).size === 1) return { row: byEhr[0], how: "ehr" };
  const key = alnum(part);
  const hit = (rows || [])
    .map((r) => ({ row: r, key: alnum(r.patient_name) }))
    .filter((c) => c.key && key.startsWith(c.key))
    .sort((a, b) => b.key.length - a.key.length)[0];
  return hit ? { row: hit.row, how: "name" } : null;
}

/** transcription-backups `<prefix>_AudioN.json` ↔ audio `<prefix>_AudioN_<iso>.webm`. */
export function jsonBelongsToLeaf(jsonName, leaf) {
  const j = String(jsonName || "").replace(/(_copilot)?\.json$/i, "").toLowerCase();
  const a = String(leaf || "").replace(AUDIO_FILE_RE, "").toLowerCase();
  return Boolean(j) && (a === j || a.startsWith(`${j}_`));
}

/** `{schema, date}` when `path` (segments) is or is inside a `<schema>/<yyyy>/<day>` folder. */
export function dayFolderOf(segments) {
  const [schema, year, day] = segments || [];
  return schema && /^\d{4}$/.test(year || "") && DAY_RE.test(day || "") ? { schema, date: day } : null;
}

/**
 * One explorer entry for a file in `bucket` at `folder`, resolved against
 * that day folder's chart rows: who links it (`linkedRow`, audio only), who
 * its filename names (`fileOwner`), and the linked transcripts.
 */
export function describeFile(bucket, folder, file, folderRows) {
  const path = folder ? `${folder}/${file.name}` : file.name;
  const isJson = bucket === "transcription-backups";
  const fileKey = `${bucket}/${path}`;
  let linkedRow = null;
  let entry = null;
  if (!isJson) {
    for (const r of folderRows || []) {
      const hit = (Array.isArray(r.more_audios) ? r.more_audios : []).find((a) =>
        entryFileKeys(a).includes(fileKey),
      );
      if (hit) {
        linkedRow = r;
        entry = hit;
        break;
      }
    }
  }
  const fileOwner = matchFileOwner(file.name, folderRows);
  const url = publicUrl(bucket, path);
  const transcripts = linkedRow
    ? (linkedRow.transcripts || []).filter((t) => !t?.is_ai_recap && transcriptBelongsToAudio(entry, t))
    : [];
  return {
    key: fileKey,
    type: isJson ? "json" : "audio",
    bucket,
    path,
    name: file.name,
    url,
    size: Number(file?.metadata?.size) || 0,
    created_at: file.created_at || file.updated_at || null,
    linkedRow,
    entry,
    fileOwner,
    transcripts,
    misfiled: Boolean(linkedRow && fileOwner && patientIdentity(fileOwner.row) !== patientIdentity(linkedRow)),
    // On a chart, but the entry stores a signed (temporary) link: rename-audio
    // looks entries up by url, so it can't rename this one.
    staleLink: Boolean(entry && isSignedUrl(entry.url || entry.audio_url)),
    folderDate: dayFolderOf(String(folder || "").split("/"))?.date || null,
  };
}

/**
 * The selected day's recordings whose filename names a different scheduled
 * patient than the chart (or file) they're on — from the Patients-tab
 * summaries, as explorer entries ready to move.
 */
export function suggestedFixes(summaries, rows) {
  const out = [];
  for (const s of summaries || []) {
    const groups = [
      [s.recordings, true],
      [s.duplicates, false],
      [s.backups, false],
    ];
    for (const [list, onChart] of groups) {
      for (const r of list || []) {
        const { bucket, path } = storageRef(r.audio);
        if (!bucket || !path) continue;
        const name = path.split("/").pop();
        const fileOwner = matchFileOwner(name, rows);
        if (!fileOwner || patientIdentity(fileOwner.row) === patientIdentity(s.row)) continue;
        out.push({
          key: `${bucket}/${path}`,
          type: "audio",
          bucket,
          path,
          name,
          url: r.audio.url,
          size: Number(r.audio.file_size) || 0,
          created_at: r.audio.created_at || null,
          linkedRow: onChart ? s.row : null,
          filedFor: s.row,
          fileOwner,
          transcripts: r.transcripts || [],
          misfiled: true,
          folderDate: dayFolderOf(path.split("/"))?.date || null,
        });
      }
    }
  }
  return out.sort((a, b) => audioMillis(a) - audioMillis(b));
}

/** Targets for a move, best first: the filename's patient, same provider, then the rest. */
export function rankTargets(item, rows) {
  const linked = rowKey(item?.linkedRow);
  const named = item?.fileOwner ? patientIdentity(item.fileOwner.row) : null;
  const schema = item?.linkedRow?._schema || String(item?.path || "").split("/")[0];
  const score = (r) => (patientIdentity(r) === named ? 0 : 2) + (r._schema === schema ? 0 : 1);
  return (rows || [])
    .filter((r) => rowKey(r) !== linked)
    .sort((a, b) => score(a) - score(b) || String(a.patient_name || "").localeCompare(String(b.patient_name || "")));
}

/** Search words: "Doe, John" / "Doe_John" → ["Doe", "John"]; PostgREST-unsafe characters dropped. */
export const searchTokens = (q) =>
  String(q || "")
    .split(/[\s,_]+/)
    .map((t) => t.replace(/[%*().\\"']/g, ""))
    .filter(Boolean);

/**
 * What to search for to find the patient a filename names: its EHR when the
 * owner part ends in `-<EHR>` (with a digit), else the name words.
 */
export function filenameSearchQuery(name) {
  const stem = String(name || "").replace(AUDIO_FILE_RE, "").replace(/(_copilot)?\.json$/i, "");
  const m = stem.match(OWNER_RE);
  const part = (m ? m[1] : "").replace(MOVED_SUFFIX_RE, "");
  if (!part) return "";
  const dash = part.lastIndexOf("-");
  const ehr = dash > 0 ? part.slice(dash + 1) : "";
  if (ehr && /\d/.test(ehr)) return ehr;
  // Recorder files with an empty EHR end in "-": drop it.
  return searchTokens(part.replace(/-+$/, "")).join(" ");
}

/**
 * Patients (visit rows) matching `q` in every schema: one word matches name
 * or EHR, several words must all appear in the name. Newest visits first.
 */
export async function searchPatients(sb, schemas, q, limit = 25) {
  const tokens = searchTokens(q);
  if (!tokens.length) return [];
  const perSchema = await Promise.all(
    (schemas || []).map(async (schema) => {
      try {
        let query = sb.schema(schema).from(tableName).select("id, patient_name, EHR_ID, date_");
        if (tokens.length === 1) {
          query = query.or(`patient_name.ilike.%${tokens[0]}%,EHR_ID.ilike.%${tokens[0]}%`);
        } else {
          for (const t of tokens) query = query.ilike("patient_name", `%${t}%`);
        }
        const { data, error } = await query.order("date_", { ascending: false }).limit(limit);
        return error ? [] : (data || []).map((r) => ({ ...r, _schema: schema }));
      } catch {
        return [];
      }
    }),
  );
  return perSchema.flat().sort((a, b) => String(b.date_ || "").localeCompare(String(a.date_ || "")));
}

/** Debounced searchPatients for a text box. */
function usePatientSearch(query, schemas) {
  const [state, setState] = useState({ results: [], loading: false, searched: "" });
  useEffect(() => {
    const q = String(query || "").trim();
    if (!q || !schemas?.length) {
      setState({ results: [], loading: false, searched: "" });
      return undefined;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    const timer = setTimeout(async () => {
      const sb = await getSupabase();
      const results = await searchPatients(sb, schemas, q);
      if (!cancelled) setState({ results, loading: false, searched: q });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, schemas]);
  return state;
}

const authHeaders = async () => {
  const { data: { session } = {} } = await supabasePublic().auth.getSession();
  return {
    "Content-Type": "application/json",
    ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
  };
};

/** One move (or its dry run) through the backend, audio or transcript JSON. */
export async function moveFile(item, target, { attach, dryRun }) {
  const isJson = item.type === "json";
  const source = item.linkedRow ? { schema: item.linkedRow._schema, patient_id: item.linkedRow.id } : null;
  const body = isJson
    ? { file_path: item.path, target: { schema: target._schema, patient_id: target.id }, attach, dry_run: dryRun }
    : {
        audio_url: item.url,
        source,
        target: { schema: target._schema, patient_id: target.id },
        attach,
        dry_run: dryRun,
        created_at: item.created_at || null,
        file_size: item.size || null,
      };
  const resp = await fetch(
    `${getBackendURL()}/api/transcription-backup/${isJson ? "move-transcript" : "move-audio"}/`,
    { method: "POST", headers: await authHeaders(), body: JSON.stringify(body) },
  );
  const result = await resp.json().catch(() => ({}));
  if (result.error === "doctor_role_required") {
    throw new Error(`Doctor role required to move recordings${result.your_role ? ` (you are ${result.your_role})` : ""}.`);
  }
  if (!resp.ok || !result.success) throw new Error(result.error || `Move failed (${resp.status})`);
  return result;
}

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "short", timeStyle: "short" }) : "");
const fmtSize = (n) => {
  const b = Number(n) || 0;
  if (!b) return "";
  return b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;
};
const patientLabel = (r) => `${r.patient_name || "Unnamed"}${r.EHR_ID ? ` (${r.EHR_ID})` : ""} · ${r._schema}`;

function JsonPreview({ path }) {
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const sb = await getSupabase();
        const { data, error } = await sb.storage.from("transcription-backups").download(path);
        if (error || !data) throw new Error(error?.message || "download failed");
        const j = JSON.parse(await data.text());
        const meta = j.metadata || {};
        if (!cancelled) {
          setState({
            loading: false,
            text: transcriptToText(j) || j.transcript_text || j.text || "",
            info: [
              j.patient_name || meta.patient_name ? `patient: ${j.patient_name || meta.patient_name}` : null,
              j.patient_id || meta.patient_id ? `id: ${j.patient_id || meta.patient_id}` : null,
              j.session_id || meta.session_id ? `session: ${j.session_id || meta.session_id}` : null,
              j.audio_url || meta.audio_url ? `audio: ${String(j.audio_url || meta.audio_url).split("/").pop()}` : null,
            ].filter(Boolean),
          });
        }
      } catch (err) {
        if (!cancelled) setState({ loading: false, error: err?.message || "could not read" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [path]);
  if (state.loading) return <Spinner size="sm" />;
  if (state.error) return <Text fontSize="xs" color="red.500">{state.error}</Text>;
  return (
    <Box>
      {state.info.length > 0 && (
        <Text fontSize="xs" color="gray.500" mb={1} wordBreak="break-all">{state.info.join(" · ")}</Text>
      )}
      <Text fontSize="sm" whiteSpace="pre-wrap" maxH="220px" overflowY="auto" color={state.text ? "gray.700" : "gray.400"}>
        {state.text || "No transcript text in this backup."}
      </Text>
    </Box>
  );
}

/** What "restore" means for a file not on a chart, or null when it doesn't apply. */
export function restoreLabel(item) {
  if (item.type === "json") return "Restore transcript…";
  if (item.linkedRow) return null;
  return item.bucket === "backup-audio" ? "Restore to chart…" : "Add to chart…";
}

function FileRow({ item, selected, onSelect, readOnly, onPickTarget, onRestore, onRename, onOpenJsonFolder }) {
  const [open, setOpen] = useState(false);
  return (
    <Box
      borderBottomWidth="1px"
      px={2}
      py={1.5}
      bg={item.misfiled ? "red.50" : selected ? "purple.50" : undefined}
      draggable={!readOnly}
      onDragStart={readOnly ? undefined : (e) => e.dataTransfer.setData("text/plain", item.key)}
    >
      <Flex align="center" gap={2} wrap="wrap">
        {!readOnly && <Checkbox isChecked={selected} onChange={(e) => onSelect(e.target.checked)} />}
        <Text fontSize="sm" title={item.type === "json" ? "Transcript backup" : "Audio"}>{item.type === "json" ? "📄" : "🎵"}</Text>
        <Text
          fontSize="sm"
          fontWeight="medium"
          wordBreak="break-all"
          flex="1"
          minW="220px"
          cursor="pointer"
          onClick={() => setOpen((o) => !o)}
        >
          {item.name}
        </Text>
        <Text fontSize="xs" color="gray.500" minW="70px" textAlign="right">{fmtSize(item.size)}</Text>
        <Text fontSize="xs" color="gray.500" minW="120px" textAlign="right">{fmtWhen(item.created_at)}</Text>
      </Flex>
      <Flex align="center" gap={2} mt={1} pl={readOnly ? 0 : 6} wrap="wrap">
        {item.linkedRow ? (
          <Badge colorScheme="purple" textTransform="none">On chart: {patientLabel(item.linkedRow)}</Badge>
        ) : item.type === "audio" ? (
          <Badge colorScheme="gray" textTransform="none" title="No chart for this folder's day lists this file">
            Not on {item.folderDate ? `${item.folderDate}'s` : "any listed"} charts
          </Badge>
        ) : null}
        {item.fileOwner && (!item.linkedRow || item.misfiled) && (
          <Badge colorScheme={item.misfiled ? "red" : "yellow"} textTransform="none">
            {item.misfiled ? "filename says" : "filename:"} {item.fileOwner.row.patient_name}
          </Badge>
        )}
        {item.staleLink && (
          <Badge colorScheme="orange" textTransform="none" title="The chart entry stores a temporary signed link to this recording.">
            temporary link
          </Badge>
        )}
        {isMovedName(item.name) && (
          <Badge colorScheme="cyan" title="Moved here from another patient by the Bucket view">moved</Badge>
        )}
        {item.type === "audio" && item.linkedRow && (
          <Badge colorScheme={item.transcripts.length ? "green" : "gray"}>
            {item.transcripts.length} transcript{item.transcripts.length === 1 ? "" : "s"}
          </Badge>
        )}
        <Box flex="1" />
        <Button size="xs" variant="ghost" onClick={() => setOpen((o) => !o)}>
          {open ? "Hide" : item.type === "json" ? "View" : "Play"}
        </Button>
        {!readOnly && (
          <>
            <Button size="xs" variant="ghost" onClick={onRename}>Rename</Button>
            {restoreLabel(item) && (
              <Button size="xs" colorScheme="green" variant="outline" onClick={onRestore}>
                {restoreLabel(item)}
              </Button>
            )}
            <Button size="xs" colorScheme="purple" variant="outline" onClick={onPickTarget}>
              Move to…
            </Button>
          </>
        )}
      </Flex>
      <Collapse in={open} animateOpacity unmountOnExit>
        <VStack align="stretch" spacing={2} mt={2} pl={readOnly ? 0 : 6}>
          {item.type === "json" ? (
            <JsonPreview path={item.path} />
          ) : (
            <>
              <Flex justify="center" overflowX="auto">
                <AudioPlayer
                  audioUrl={item.url}
                  patientId={item.linkedRow?.id}
                  audioSavedAt={item.created_at}
                  audioDisplayName={item.name}
                />
              </Flex>
              {item.transcripts.map((t, i) => (
                <Box key={t.id || t.session_id || i} borderWidth="1px" borderRadius="md" p={2}>
                  <HStack mb={1}>
                    <Badge colorScheme="blue">{transcriptShowsAsLive(t) ? "Live" : "Corti"}</Badge>
                    {t.created_at && <Text fontSize="xs" color="gray.500">{new Date(t.created_at).toLocaleString()}</Text>}
                  </HStack>
                  <Text fontSize="sm" whiteSpace="pre-wrap" maxH="180px" overflowY="auto">
                    {transcriptToText(t) || "This transcript row has no text."}
                  </Text>
                </Box>
              ))}
              {onOpenJsonFolder && (
                <Button size="xs" variant="link" alignSelf="flex-start" onClick={onOpenJsonFolder}>
                  Open this folder in transcription-backups →
                </Button>
              )}
            </>
          )}
        </VStack>
      </Collapse>
    </Box>
  );
}

const visitDay = (r) => String(r?.date_ || "").slice(0, 10);

/** One patient visit as a pick/drop target. */
function PatientResultRow({ row, tags = [], disabled, onPick, dropProps = {} }) {
  return (
    <Flex
      align="center"
      gap={2}
      px={2}
      py={1.5}
      borderRadius="md"
      borderWidth="1px"
      borderColor="transparent"
      cursor={disabled ? "not-allowed" : "pointer"}
      opacity={disabled ? 0.5 : 1}
      _hover={disabled ? undefined : { bg: "purple.50" }}
      onClick={disabled ? undefined : onPick}
      {...dropProps}
    >
      <Box flex="1" minW={0}>
        <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>{row.patient_name || "Unnamed patient"}</Text>
        <HStack spacing={1} wrap="wrap">
          <Badge colorScheme="teal" variant="outline" textTransform="none" fontSize="9px">{row._schema}</Badge>
          {row.EHR_ID && <Text fontSize="10px" color="gray.500">{row.EHR_ID}</Text>}
          {visitDay(row) && <Text fontSize="10px" color="gray.600">visit {visitDay(row)}</Text>}
          {tags.map(([label, color]) => (
            <Badge key={label} fontSize="9px" colorScheme={color} textTransform="none">{label}</Badge>
          ))}
        </HStack>
      </Box>
    </Flex>
  );
}

/**
 * "Move to…": pick ANY patient visit — the selected day's patients, or a
 * search across every provider schema and every date. With no search typed,
 * the patient the filename names is looked up automatically.
 */
export function TargetPicker({ items, dayRows, schemas, date, onPick, onClose, verb = "Move" }) {
  const [query, setQuery] = useState("");
  const [schemaFilter, setSchemaFilter] = useState("");
  const first = items[0];
  const autoQuery = useMemo(() => (items.length === 1 ? filenameSearchQuery(first?.name) : ""), [items, first]);
  const searchSchemas = useMemo(() => (schemaFilter ? [schemaFilter] : schemas), [schemaFilter, schemas]);
  const typed = usePatientSearch(query, searchSchemas);
  const auto = usePatientSearch(query ? "" : autoQuery, searchSchemas);
  const linkedKeys = new Set(items.map((it) => rowKey(it.linkedRow)).filter(Boolean));
  const named = first?.fileOwner ? patientIdentity(first.fileOwner.row) : null;
  const fileDays = new Set(items.map((it) => it.folderDate).filter(Boolean));

  const tagsFor = (r) => {
    const tags = [];
    if (named && patientIdentity(r) === named) tags.push(["★ filename", "yellow"]);
    if (visitDay(r) === date) tags.push(["selected day", "purple"]);
    if (fileDays.has(visitDay(r))) tags.push(["file's day", "orange"]);
    if (linkedKeys.has(rowKey(r))) tags.push(["file is here now", "gray"]);
    return tags;
  };
  const renderRows = (rows) =>
    rows.map((r) => (
      <PatientResultRow
        key={rowKey(r)}
        row={r}
        tags={tagsFor(r)}
        disabled={items.length === 1 && linkedKeys.has(rowKey(r))}
        onPick={() => onPick(r)}
      />
    ));
  const dayList = rankTargets(first, (dayRows || []).filter((r) => !schemaFilter || r._schema === schemaFilter));

  return (
    <Modal isOpen onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {verb} {items.length === 1 ? first.name : `${items.length} files`} to…
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex gap={2} mb={3}>
            <Input
              autoFocus
              size="sm"
              placeholder="Search any patient — name or EHR, every provider and date"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <Select size="sm" maxW="180px" value={schemaFilter} onChange={(e) => setSchemaFilter(e.target.value)}>
              <option value="">All providers</option>
              {(schemas || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </Flex>
          {query.trim() ? (
            <Box>
              <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.500" mb={1}>
                Search results {typed.loading && <Spinner size="xs" ml={1} />}
              </Text>
              {!typed.loading && typed.searched && typed.results.length === 0 && (
                <Text fontSize="sm" color="gray.500">No patient matches “{typed.searched}”.</Text>
              )}
              <VStack align="stretch" spacing={0}>{renderRows(typed.results)}</VStack>
            </Box>
          ) : (
            <VStack align="stretch" spacing={4}>
              {autoQuery && (
                <Box>
                  <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.500" mb={1}>
                    Patients matching the filename (“{autoQuery}”) {auto.loading && <Spinner size="xs" ml={1} />}
                  </Text>
                  {!auto.loading && auto.searched && auto.results.length === 0 && (
                    <Text fontSize="sm" color="gray.500">None found in {schemaFilter || "any provider"}.</Text>
                  )}
                  <VStack align="stretch" spacing={0}>{renderRows(auto.results.slice(0, 8))}</VStack>
                </Box>
              )}
              <Box>
                <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.500" mb={1}>
                  Scheduled on {date}
                </Text>
                {dayList.length === 0 && <Text fontSize="sm" color="gray.500">Nobody scheduled.</Text>}
                <VStack align="stretch" spacing={0}>{renderRows(dayList)}</VStack>
              </Box>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

async function postJson(path, body) {
  const resp = await fetch(`${getBackendURL()}/api/transcription-backup/${path}/`, {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify(body),
  });
  const result = await resp.json().catch(() => ({}));
  if (result.error === "doctor_role_required") throw new Error("Doctor role required.");
  if (!resp.ok || result.success === false) throw new Error(result.error || `Request failed (${resp.status})`);
  return result;
}

/** Request body for /restore/ — the same one the chart's Backups tab sends. */
export function restoreBody(item, target) {
  const folderSchema = String(item.path || "").split("/")[0] || target._schema;
  const common = {
    patient_id: target.id,
    bucket: item.bucket,
    file_path: item.path,
    schema_name: folderSchema,
    patient_schema_name: target._schema,
    patient_display_name: target.patient_name || undefined,
    metadata: {},
  };
  return item.type === "json"
    ? { ...common, is_audio_backup: false }
    : { ...common, is_audio_backup: true, audio_url: item.url };
}

function RestoreDialog({ item, target, onClose, onDone }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const isJson = item.type === "json";
  const run = async () => {
    setBusy(true);
    try {
      const result = await postJson("restore", restoreBody(item, target));
      clearBackupCache(target.id);
      const r = result.restored_data || {};
      toast({
        title: `Restored to ${target.patient_name}`,
        description:
          [r.restored_audio && "recording added to the chart", r.restored_transcript && "transcript added"]
            .filter(Boolean)
            .join(", ") || undefined,
        status: "success",
        duration: 6000,
        isClosable: true,
      });
      onDone();
    } catch (err) {
      toast({ title: "Restore failed", description: err.message, status: "error", duration: 8000, isClosable: true });
      setBusy(false);
    }
  };
  return (
    <Modal isOpen onClose={busy ? () => {} : onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isJson ? "Restore transcript" : "Restore recording"}</ModalHeader>
        {!busy && <ModalCloseButton />}
        <ModalBody>
          <VStack align="stretch" spacing={2} fontSize="sm">
            <Text wordBreak="break-all">{isJson ? "📄" : "🎵"} <b>{item.name}</b></Text>
            <Text color="gray.600" wordBreak="break-all">{item.bucket}/{item.path}</Text>
            <Text>
              → {patientLabel(target)}
              {visitDay(target) ? ` · visit ${visitDay(target)}` : ""}
            </Text>
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              {isJson
                ? "Adds this transcript to the chart. The file stays where it is."
                : "Adds this recording to the chart’s audios, plus its transcript backup when one is found. The file stays where it is — use Move to re-file it under the patient."}
            </Alert>
            {item.fileOwner && patientIdentity(item.fileOwner.row) !== patientIdentity(target) && (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                The filename names {item.fileOwner.row.patient_name}, not {target.patient_name}.
              </Alert>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={onClose} isDisabled={busy}>Cancel</Button>
          <Button colorScheme="green" onClick={run} isLoading={busy}>Restore</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/** Extension the rename keeps (backup sidecars keep `_metadata.json`). */
export const keptExtension = (name) =>
  (String(name || "").match(/(_metadata\.json|\.json|\.(webm|mp3|wav|m4a|ogg|mp4))$/i) || [""])[0];

function RenameDialog({ item, onClose, onDone }) {
  const toast = useToast();
  const ext = keptExtension(item.name);
  const [stem, setStem] = useState(item.name.slice(0, item.name.length - ext.length));
  const [busy, setBusy] = useState(false);
  const clean = stem.trim();
  const run = async () => {
    setBusy(true);
    try {
      // Chart audios go through rename-audio so the chart, its transcripts and
      // their backup JSON follow; anything else is a plain storage rename.
      if (item.linkedRow) {
        await postJson("rename-audio", {
          patient_id: item.linkedRow.id,
          old_audio_url: item.url,
          new_filename: clean,
          schema_name: item.linkedRow._schema,
        });
        clearBackupCache(item.linkedRow.id);
      } else {
        await postJson("rename-file", { bucket: item.bucket, path: item.path, new_name: clean });
      }
      toast({ title: "Renamed", status: "success", duration: 3000 });
      onDone();
    } catch (err) {
      toast({ title: "Rename failed", description: err.message, status: "error", duration: 8000, isClosable: true });
      setBusy(false);
    }
  };
  return (
    <Modal isOpen onClose={busy ? () => {} : onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Rename file</ModalHeader>
        {!busy && <ModalCloseButton />}
        <ModalBody>
          <VStack align="stretch" spacing={2}>
            <Text fontSize="sm" color="gray.600" wordBreak="break-all">{item.bucket}/{item.path}</Text>
            <HStack>
              <Input size="sm" autoFocus aria-label="New file name" value={stem} onChange={(e) => setStem(e.target.value)} />
              <Text fontSize="sm" color="gray.500">{ext}</Text>
            </HStack>
            <Text fontSize="xs" color="gray.500">
              {item.linkedRow
                ? `On ${item.linkedRow.patient_name}’s chart — the chart entry, its transcripts and transcript backups are updated too.`
                : "Not on any chart — only the stored file is renamed."}{" "}
              Letters, numbers, _ - . and , are kept; other characters become _.
              {item.staleLink && (
                <Text as="span" color="orange.600" display="block" mt={1}>
                  This chart entry stores a temporary link, so it can’t be renamed here.
                </Text>
              )}
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={onClose} isDisabled={busy}>Cancel</Button>
          <Button
            colorScheme="purple"
            onClick={run}
            isLoading={busy}
            isDisabled={!clean || `${clean}${ext}` === item.name || item.staleLink}
          >
            Rename
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export function MoveDialog({ move, onClose, onDone }) {
  const toast = useToast();
  const [plans, setPlans] = useState({});
  const [attach, setAttach] = useState({});
  const [running, setRunning] = useState(false);
  const { items, target } = move;

  useEffect(() => {
    // Audio-bucket files and transcripts land on the chart; backups stay backups.
    const defaults = Object.fromEntries(items.map((it) => [it.key, it.bucket !== "backup-audio"]));
    setAttach(defaults);
    setPlans({});
    let cancelled = false;
    (async () => {
      for (const it of items) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const plan = await moveFile(it, target, { attach: defaults[it.key], dryRun: true });
          if (!cancelled) setPlans((p) => ({ ...p, [it.key]: plan }));
        } catch (err) {
          if (!cancelled) setPlans((p) => ({ ...p, [it.key]: { error: err.message } }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [items, target]);

  const ready = Object.keys(plans).length === items.length;
  const movable = items.filter((it) => plans[it.key] && !plans[it.key].error);
  const crossProvider = items.some((it) => String(it.path).split("/")[0] !== target._schema);
  const otherName = items.filter((it) => it.fileOwner && patientIdentity(it.fileOwner.row) !== patientIdentity(target));

  const run = async () => {
    setRunning(true);
    const done = [];
    const failed = [];
    for (const it of movable) {
      try {
        // eslint-disable-next-line no-await-in-loop
        done.push(await moveFile(it, target, { attach: attach[it.key], dryRun: false }));
        const src = plans[it.key]?.source?.patient_id;
        if (src) clearBackupCache(src);
      } catch (err) {
        failed.push(`${it.name}: ${err.message}`);
      }
    }
    clearBackupCache(target.id);
    setRunning(false);
    const warnings = done.flatMap((d) => d.warnings || []);
    toast({
      title: `Moved ${done.length} of ${items.length} to ${target.patient_name}`,
      description: [...failed, ...warnings].join("\n") || undefined,
      status: failed.length ? "warning" : "success",
      duration: 8000,
      isClosable: true,
    });
    onDone();
  };

  return (
    <Modal isOpen onClose={running ? () => {} : onClose} size="3xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Move {items.length} file{items.length === 1 ? "" : "s"} to {target.patient_name}
          <Text fontSize="sm" fontWeight="normal" color="gray.500">
            {patientLabel(target)} · visit {String(target.date_ || "").slice(0, 10)}
          </Text>
        </ModalHeader>
        {!running && <ModalCloseButton />}
        <ModalBody>
          <VStack align="stretch" spacing={3}>
            {crossProvider && (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                Files go into {target._schema}’s folder for this visit.
              </Alert>
            )}
            {otherName.length > 0 && (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                {otherName.length === 1 ? "The filename names" : `${otherName.length} filenames name`}{" "}
                {[...new Set(otherName.map((it) => it.fileOwner.row.patient_name))].join(", ")}, not{" "}
                {target.patient_name}. Listen before moving.
              </Alert>
            )}
            {items.map((it) => {
              const plan = plans[it.key];
              const fromDate = plan?.source?.date || it.folderDate;
              return (
                <Box key={it.key} borderWidth="1px" borderRadius="md" p={3}>
                  <Text fontSize="sm" fontWeight="semibold" wordBreak="break-all">
                    {it.type === "json" ? "📄" : "🎵"} {it.name}
                  </Text>
                  {!plan ? (
                    <HStack mt={2}><Spinner size="xs" /><Text fontSize="xs">Checking…</Text></HStack>
                  ) : plan.error ? (
                    <Text mt={2} fontSize="sm" color="red.600">Can’t move: {plan.error}</Text>
                  ) : (
                    <VStack align="stretch" spacing={1} mt={2} fontSize="sm">
                      <Text color="gray.600">
                        From{" "}
                        {plan.source
                          ? `${plan.source.patient_name} (${plan.source.schema}${plan.source.date ? `, ${plan.source.date}` : ""})${plan.source.detected ? " — found by the server" : ""}`
                          : "no chart"}
                      </Text>
                      <Text wordBreak="break-all">→ <b>{plan.to_path}</b></Text>
                      {fromDate && plan.target_date && fromDate !== plan.target_date && (
                        <Badge alignSelf="flex-start" colorScheme="orange" textTransform="none">
                          moves from {fromDate} to the {plan.target_date} visit
                        </Badge>
                      )}
                      {it.type === "audio" ? (
                        <>
                          <Text>
                            {plan.transcripts_moving} transcript{plan.transcripts_moving === 1 ? "" : "s"} move with it, plus
                            any transcript backup files.
                          </Text>
                          {plan.was_on_chart ? (
                            <Text color="gray.600">Becomes Audio {plan.audio_number} on {target.patient_name}’s chart.</Text>
                          ) : (
                            <Checkbox
                              size="sm"
                              isChecked={Boolean(attach[it.key])}
                              onChange={(e) => setAttach((a) => ({ ...a, [it.key]: e.target.checked }))}
                            >
                              Add to {target.patient_name}’s chart audios
                            </Checkbox>
                          )}
                        </>
                      ) : (
                        <>
                          {plan.already_on_target ? (
                            <Text color="gray.600">{target.patient_name} already has this transcript; only the file moves.</Text>
                          ) : (
                            <Checkbox
                              size="sm"
                              isChecked={Boolean(attach[it.key])}
                              onChange={(e) => setAttach((a) => ({ ...a, [it.key]: e.target.checked }))}
                            >
                              Add as a transcript on {target.patient_name}’s chart
                              {plan.transcripts_moving ? ` (moves ${plan.transcripts_moving} from the source chart)` : ""}
                            </Checkbox>
                          )}
                          {plan.audio_stays_on_source && (
                            <Text fontSize="xs" color="orange.600">
                              Its recording stays on {plan.source?.patient_name}. To move both, move the audio file instead.
                            </Text>
                          )}
                        </>
                      )}
                    </VStack>
                  )}
                </Box>
              );
            })}
            {items.length > 1 && (
              <Text fontSize="xs" color="gray.500">Files move one at a time; audio numbers are assigned as each lands.</Text>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={onClose} isDisabled={running}>Cancel</Button>
          <Button colorScheme="purple" onClick={run} isLoading={running} isDisabled={!ready || movable.length === 0}>
            Move {movable.length}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default function AudioBucketView({
  summaries,
  allRows,
  date,
  providerSchemas,
  provider,
  readOnly,
  onChanged,
}) {
  const startSchema = provider && provider !== "__all__" ? provider : null;
  const [bucket, setBucket] = useState("audio");
  const [segments, setSegments] = useState(() => (startSchema && date ? [startSchema, date.slice(0, 4), date] : []));
  const [listing, setListing] = useState({ folders: [], files: [], more: false });
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState("");
  const [folderRows, setFolderRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [move, setMove] = useState(null);
  const [dropOver, setDropOver] = useState(null);
  const [picker, setPicker] = useState(null); // { items, mode: "move" | "restore" } waiting for a target
  const [restore, setRestore] = useState(null); // { item, target } waiting for confirmation
  const [renaming, setRenaming] = useState(null); // item
  const [leftQuery, setLeftQuery] = useState("");
  const leftSearch = usePatientSearch(leftQuery, providerSchemas);
  const [showFixes, setShowFixes] = useState(true);
  const [reloadTick, setReloadTick] = useState(0);
  const rowsCache = useRef(new Map());

  const folder = segments.join("/");
  const day = dayFolderOf(segments);

  // Folder listing (folders have no id in Storage's list response).
  const listRequest = useRef(0);
  const list = useCallback(
    async (offset = 0) => {
      const req = ++listRequest.current;
      setListLoading(true);
      setListError("");
      if (!offset) setListing({ folders: [], files: [], more: false });
      try {
        const sb = await getSupabase();
        const { data, error } = await sb.storage
          .from(bucket)
          .list(folder, { limit: PAGE, offset, sortBy: { column: "name", order: "asc" } });
        if (error) throw error;
        if (req !== listRequest.current) return;
        const entries = data || [];
        const folders = entries.filter((f) => !f.id).map((f) => f.name);
        const files = entries.filter((f) => f.id && f.name !== ".emptyFolderPlaceholder");
        setListing((prev) =>
          offset
            ? { folders: [...prev.folders, ...folders], files: [...prev.files, ...files], more: entries.length === PAGE }
            : { folders, files, more: entries.length === PAGE },
        );
      } catch (err) {
        if (req === listRequest.current) setListError(err?.message || "Could not list this folder.");
      } finally {
        if (req === listRequest.current) setListLoading(false);
      }
    },
    [bucket, folder],
  );
  useEffect(() => {
    setSelected(new Set());
    list(0);
  }, [list, reloadTick]);

  // A day folder's charts — who links each file, and its transcripts.
  useEffect(() => {
    if (!day) {
      setFolderRows([]);
      return undefined;
    }
    const cacheKey = `${day.schema}|${day.date}`;
    if (rowsCache.current.has(cacheKey)) {
      setFolderRows(rowsCache.current.get(cacheKey));
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const sb = await getSupabase();
        const { data, error } = await sb
          .schema(day.schema)
          .from(tableName)
          .select("id, patient_name, EHR_ID, date_, more_audios, transcripts")
          .eq("date_", day.date);
        const rows = error ? [] : (data || []).map((r) => ({ ...r, _schema: day.schema }));
        rowsCache.current.set(cacheKey, rows);
        if (!cancelled) setFolderRows(rows);
      } catch {
        if (!cancelled) setFolderRows([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [day?.schema, day?.date, reloadTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const items = useMemo(
    () =>
      listing.files
        .filter((f) => !filter || f.name.toLowerCase().includes(filter.toLowerCase()))
        // The selected day's patients first: a file naming someone scheduled
        // today resolves to the row it would be moved to.
        .map((f) => describeFile(bucket, folder, f, [...allRows, ...folderRows])),
    [listing.files, filter, bucket, folder, folderRows, allRows],
  );
  const shownFolders = useMemo(
    () => listing.folders.filter((n) => !filter || n.toLowerCase().includes(filter.toLowerCase())),
    [listing.folders, filter],
  );
  const fixes = useMemo(() => suggestedFixes(summaries, allRows), [summaries, allRows]);
  const itemByKey = useMemo(() => new Map([...fixes, ...items].map((it) => [it.key, it])), [fixes, items]);
  const selectedItems = [...selected].map((k) => itemByKey.get(k)).filter(Boolean);

  const go = (segs) => {
    setFilter("");
    setSegments(segs);
  };
  const startMove = (moveItems, target) => {
    const valid = moveItems.filter((it) => rowKey(it.linkedRow) !== rowKey(target));
    if (valid.length && target) setMove({ items: valid, target });
  };
  const onDropOn = (row) => (e) => {
    e.preventDefault();
    setDropOver(null);
    if (readOnly) return;
    const key = e.dataTransfer.getData("text/plain");
    const dragged = itemByKey.get(key);
    if (!dragged) return;
    // Dragging a selected file moves the whole selection.
    startMove(selected.has(key) ? selectedItems : [dragged], row);
  };
  const toggle = (key, on) =>
    setSelected((s) => {
      const n = new Set(s);
      if (on) n.add(key);
      else n.delete(key);
      return n;
    });

  const targetsOnLeft = summaries.map((s) => s.row);
  // After any write: re-list the folder, re-read its charts, reload the day.
  const refreshAfterChange = () => {
    setSelected(new Set());
    rowsCache.current.clear();
    setReloadTick((t) => t + 1);
    onChanged();
  };
  const dropProps = (row) => {
    const k = rowKey(row);
    return {
      borderColor: dropOver === k ? "purple.400" : "transparent",
      bg: dropOver === k ? "purple.100" : undefined,
      onDragOver: readOnly ? undefined : (e) => { e.preventDefault(); setDropOver(k); },
      onDragLeave: () => setDropOver((d) => (d === k ? null : d)),
      onDrop: onDropOn(row),
    };
  };

  return (
    <VStack align="stretch" spacing={3}>
      {fixes.length > 0 && (
        <Box borderWidth="1px" borderColor="red.200" bg="red.50" borderRadius="md" p={3}>
          <Flex align="center">
            <Text fontWeight="semibold">Suggested fixes for {date} ({fixes.length})</Text>
            <Box flex="1" />
            <Button size="xs" variant="ghost" onClick={() => setShowFixes((v) => !v)}>{showFixes ? "Hide" : "Show"}</Button>
          </Flex>
          <Collapse in={showFixes} animateOpacity>
            <Text fontSize="xs" color="gray.600" my={2}>
              Filenames that name a different scheduled patient than the chart they’re on — usually the recorder was open
              on the wrong patient.
            </Text>
            <VStack align="stretch" spacing={1}>
              {fixes.map((it) => (
                <Flex key={it.key} align="center" gap={2} wrap="wrap" fontSize="sm">
                  <Text wordBreak="break-all" flex="1" minW="220px">
                    🎵 {it.name} — {it.linkedRow ? `on ${it.linkedRow.patient_name}’s chart` : `filed for ${it.filedFor.patient_name}`}
                  </Text>
                  {!readOnly && (
                    <Button size="xs" colorScheme="red" variant="outline" onClick={() => startMove([it], it.fileOwner.row)}>
                      Move to {it.fileOwner.row.patient_name}
                    </Button>
                  )}
                </Flex>
              ))}
            </VStack>
          </Collapse>
        </Box>
      )}

      <Flex gap={3} align="flex-start" direction={{ base: "column", md: "row" }}>
        {/* Move targets — the selected day's patients */}
        <Box w={{ base: "100%", md: "290px" }} flexShrink={0} borderWidth="1px" borderRadius="md" p={2} maxH="72vh" overflowY="auto">
          <Input
            size="sm"
            mb={2}
            placeholder="Find any patient (all providers)"
            value={leftQuery}
            onChange={(e) => setLeftQuery(e.target.value)}
          />
          {leftQuery.trim() && (
            <Box mb={3}>
              <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.500" mb={1}>
                Search results {leftSearch.loading && <Spinner size="xs" ml={1} />}
              </Text>
              {!leftSearch.loading && leftSearch.searched && leftSearch.results.length === 0 && (
                <Text fontSize="sm" color="gray.500">No match.</Text>
              )}
              <VStack align="stretch" spacing={0}>
                {leftSearch.results.map((r) => (
                  <PatientResultRow
                    key={rowKey(r)}
                    row={r}
                    tags={visitDay(r) === date ? [["selected day", "purple"]] : []}
                    disabled={readOnly || selectedItems.length === 0}
                    onPick={() => startMove(selectedItems, r)}
                    dropProps={dropProps(r)}
                  />
                ))}
              </VStack>
              <Text fontSize="10px" color="gray.500" mt={1}>
                Drop files on a visit, or select files and click one.
              </Text>
            </Box>
          )}
          <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.500">
            Patients on {date}
          </Text>
          <Text fontSize="xs" color="gray.500" mb={2}>
            Drop files here, or search above for anyone else.
          </Text>
          <VStack align="stretch" spacing={1}>
            {summaries.length === 0 && <Text fontSize="sm" color="gray.500">No patients scheduled.</Text>}
            {summaries.map((s) => {
              const k = rowKey(s.row);
              return (
                <Flex
                  key={k}
                  align="center"
                  gap={2}
                  p={2}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={dropOver === k ? "purple.400" : "transparent"}
                  bg={dropOver === k ? "purple.100" : undefined}
                  _hover={{ bg: "gray.50" }}
                  cursor="pointer"
                  title="Open this patient's day folder"
                  onClick={() => go([s.row._schema, date.slice(0, 4), date])}
                  onDragOver={readOnly ? undefined : (e) => { e.preventDefault(); setDropOver(k); }}
                  onDragLeave={() => setDropOver((d) => (d === k ? null : d))}
                  onDrop={onDropOn(s.row)}
                >
                  <Flex
                    align="center"
                    justify="center"
                    minW="30px"
                    h="28px"
                    borderRadius="md"
                    bg={s.recordings.length ? "purple.500" : "gray.100"}
                    color={s.recordings.length ? "white" : "gray.500"}
                    title={`${s.recordings.length} audio on the chart`}
                  >
                    <Text fontWeight="bold" fontSize="sm">{s.recordings.length}</Text>
                  </Flex>
                  <Box flex="1" minW={0}>
                    <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>{s.row.patient_name || "Unnamed patient"}</Text>
                    <HStack spacing={1}>
                      <Badge colorScheme="teal" variant="outline" textTransform="none" fontSize="9px">{s.row._schema}</Badge>
                      {s.row.EHR_ID && <Text fontSize="10px" color="gray.500">{s.row.EHR_ID}</Text>}
                      {s.transcripts.length > 0 && <Badge fontSize="9px" colorScheme="green">{s.transcripts.length} tr</Badge>}
                    </HStack>
                  </Box>
                </Flex>
              );
            })}
          </VStack>
        </Box>

        {/* Explorer */}
        <Box flex="1" minW={0} borderWidth="1px" borderRadius="md">
          <Flex gap={2} p={2} align="center" wrap="wrap" borderBottomWidth="1px" bg="gray.50">
            <ButtonGroup size="xs" isAttached variant="outline" colorScheme="purple">
              {BUCKETS.map((b) => (
                <Button key={b} variant={bucket === b ? "solid" : "outline"} onClick={() => setBucket(b)}>
                  {b}
                </Button>
              ))}
            </ButtonGroup>
            <Box flex="1" />
            <Input size="xs" maxW="200px" placeholder="Filter this folder" value={filter} onChange={(e) => setFilter(e.target.value)} />
            <Button size="xs" onClick={() => setReloadTick((t) => t + 1)} isLoading={listLoading}>Refresh</Button>
          </Flex>

          <Flex px={2} py={1.5} align="center" gap={2} wrap="wrap" borderBottomWidth="1px">
            <Breadcrumb fontSize="sm" separator="/">
              <BreadcrumbItem>
                <BreadcrumbLink fontWeight="semibold" onClick={() => go([])}>{bucket}</BreadcrumbLink>
              </BreadcrumbItem>
              {segments.map((seg, i) => (
                <BreadcrumbItem key={`${i}-${seg}`} isCurrentPage={i === segments.length - 1}>
                  <BreadcrumbLink onClick={() => go(segments.slice(0, i + 1))}>{seg}</BreadcrumbLink>
                </BreadcrumbItem>
              ))}
            </Breadcrumb>
            <Box flex="1" />
            {(providerSchemas || []).length > 0 && date && (
              <Wrap spacing={1}>
                <WrapItem><Text fontSize="xs" color="gray.500">Jump to {date}:</Text></WrapItem>
                {providerSchemas.map((s) => (
                  <WrapItem key={s}>
                    <Button size="xs" variant="ghost" onClick={() => go([s, date.slice(0, 4), date])}>{s}</Button>
                  </WrapItem>
                ))}
              </Wrap>
            )}
          </Flex>

          {!readOnly && selectedItems.length > 0 && (
            <Flex gap={2} align="center" p={2} bg="purple.50" wrap="wrap" borderBottomWidth="1px">
              <Text fontSize="sm" fontWeight="semibold">{selectedItems.length} selected</Text>
              <Button size="sm" colorScheme="purple" onClick={() => setPicker({ items: selectedItems, mode: "move" })}>
                Move selected to…
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
            </Flex>
          )}

          <Box maxH="62vh" overflowY="auto">
            {listError && (
              <Alert status="error" m={2} borderRadius="md" w="auto">
                <AlertIcon />
                {listError}
              </Alert>
            )}
            {segments.length > 0 && (
              <Flex px={2} py={1.5} borderBottomWidth="1px" cursor="pointer" _hover={{ bg: "gray.50" }} onClick={() => go(segments.slice(0, -1))}>
                <Text fontSize="sm" color="gray.600">⬑ ..</Text>
              </Flex>
            )}
            {shownFolders.map((name) => (
              <Flex
                key={name}
                px={2}
                py={1.5}
                gap={2}
                borderBottomWidth="1px"
                cursor="pointer"
                _hover={{ bg: "gray.50" }}
                onClick={() => go([...segments, name])}
              >
                <Text fontSize="sm">📁</Text>
                <Text fontSize="sm" fontWeight="medium">{name}</Text>
                {DAY_RE.test(name) && name === date && <Badge colorScheme="purple">selected day</Badge>}
              </Flex>
            ))}
            {items.map((it) => (
              <FileRow
                key={it.key}
                item={it}
                readOnly={readOnly}
                selected={selected.has(it.key)}
                onSelect={(on) => toggle(it.key, on)}
                onPickTarget={() => setPicker({ items: [it], mode: "move" })}
                onRestore={() => setPicker({ items: [it], mode: "restore" })}
                onRename={() => setRenaming(it)}
                onOpenJsonFolder={
                  bucket !== "transcription-backups"
                    ? () => {
                        setBucket("transcription-backups");
                        setFilter("");
                      }
                    : null
                }
              />
            ))}
            {listLoading && listing.files.length + listing.folders.length === 0 && (
              <Flex justify="center" py={8}><Spinner /></Flex>
            )}
            {!listLoading && !listError && shownFolders.length === 0 && items.length === 0 && (
              <Text color="gray.500" py={6} textAlign="center" fontSize="sm">
                {filter ? `Nothing here matches “${filter}”.` : "This folder is empty."}
              </Text>
            )}
            {listing.more && (
              <Flex justify="center" py={2}>
                <Button size="xs" onClick={() => list(listing.folders.length + listing.files.length)} isLoading={listLoading}>
                  Load more
                </Button>
              </Flex>
            )}
          </Box>
        </Box>
      </Flex>

      {picker && (
        <TargetPicker
          items={picker.items}
          verb={picker.mode === "restore" ? "Restore" : "Move"}
          dayRows={targetsOnLeft}
          schemas={providerSchemas}
          date={date}
          onClose={() => setPicker(null)}
          onPick={(row) => {
            setPicker(null);
            if (picker.mode === "restore") setRestore({ item: picker.items[0], target: row });
            else startMove(picker.items, row);
          }}
        />
      )}
      {restore && (
        <RestoreDialog
          item={restore.item}
          target={restore.target}
          onClose={() => setRestore(null)}
          onDone={() => {
            setRestore(null);
            refreshAfterChange();
          }}
        />
      )}
      {renaming && (
        <RenameDialog
          item={renaming}
          onClose={() => setRenaming(null)}
          onDone={() => {
            setRenaming(null);
            refreshAfterChange();
          }}
        />
      )}
      {move && (
        <MoveDialog
          move={move}
          onClose={() => setMove(null)}
          onDone={() => {
            setMove(null);
            refreshAfterChange();
          }}
        />
      )}
    </VStack>
  );
}
